'use client';

import BaseText from '@dvd/components/BaseText';
import FeatureSplitChild, {
  FeatureSplitMedia,
  FeatureSplitChildProps,
} from '@dvd/components/FeatureSplitChild';
import { styled } from 'styled-components';

type FeatureSplitProps = {
  /**
   * The required headline of the feature split
   */
  headline: string;

  /**
   * The features of an integration; should be one or more FeatureSplitChild components.
   * Each FeatureSplitChild displays media (video or image) alongside a headline and description
   * in a grid layout where media appears on the left and content on the right.
   */
  features: FeatureSplitChildProps[];
};

const FeatureSplitContainer = styled.div`
  display: grid;
  ${({ theme }) => theme.grid.columns.small};

  ${FeatureSplitMedia} {
    grid-column: 1 / span 2;
  }

  .FeatureSplit__base-text {
    grid-column: 1 / span 2;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    gap: ${({ theme }) => theme.spacing[2]};
    padding-block: ${({ theme }) => theme.spacing[4]};

    ${({ theme }) => theme.grid.columns.medium};

    ${FeatureSplitMedia} {
      grid-column: 1 / span 2;
    }

    .FeatureSplit__base-text {
      grid-column: 3 / span 4;
      align-self: center;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    gap: ${({ theme }) => theme.spacing[2]};
    padding-block: ${({ theme }) => theme.spacing[4]};

    ${({ theme }) => theme.grid.columns.large};

    ${FeatureSplitMedia} {
      grid-column: 1 / span 4;
    }

    .FeatureSplit__base-text {
      grid-column: 5 / span 8;
    }
  }
`;

export const FeatureSplitHeadline = styled(BaseText)`
  grid-column: 1 / span 2;

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-column: 1 / span 8;

    h3 {
      margin-block-end: 0;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    grid-column: 1 / span 12;
  }
`;

/**
 *
 * This component is used to display a feature split.
 * It uses a custom grid container with a headline and a set of features.
 * The features are displayed in a grid layout with the media on the left and the content on the right.
 *
 * Rules:
 * - This module should never be used in a section only a section split variant.
 * - The features should be objects using FeatureSplitChildProps that include media props (video/image) and content props (headline/description).
 * - The features should include a minimum of one set of media and content props.
 */
const FeatureSplit = ({ headline, features }: FeatureSplitProps) => {
  return (
    <FeatureSplitContainer>
      <FeatureSplitHeadline headingLevel="h3" heading={headline} />
      {features.map((feature) => (
        <FeatureSplitChild key={feature.headline} {...feature} />
      ))}
    </FeatureSplitContainer>
  );
};

export default FeatureSplit;
